/**
 * Форматирует дату в читаемый вид
 * @param {string|number} dateInput - ISO строка даты или Unix timestamp
 * @returns {string} Отформатированная дата (например: "12 мая 2026")
 */
export function formatDate(dateInput) {
  if (!dateInput) return '';

  let date;

  // Если это число (timestamp)
  if (typeof dateInput === 'number') {
    // Если timestamp в секундах (меньше 10^12), умножаем на 1000
    date = new Date(dateInput < 1e12 ? dateInput * 1000 : dateInput);
  } else {
    // Если это строка (ISO формат)
    date = new Date(dateInput);
  }

  if (isNaN(date.getTime())) return '';

  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Форматирует дату с временем
 * @param {string|number} dateInput - ISO строка даты или Unix timestamp
 * @returns {string} Отформатированная дата со временем (например: "12 мая 2026, 14:30")
 */
export function formatDateTime(dateInput) {
  if (!dateInput) return '';

  let date;

  if (typeof dateInput === 'number') {
    date = new Date(dateInput < 1e12 ? dateInput * 1000 : dateInput);
  } else {
    date = new Date(dateInput);
  }

  if (isNaN(date.getTime())) return '';

  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Форматирует дату в коротком виде
 * @param {string|number} dateInput - ISO строка даты или Unix timestamp
 * @returns {string} Короткая дата (например: "12.05.2026")
 */
export function formatShortDate(dateInput) {
  if (!dateInput) return '';

  let date;

  if (typeof dateInput === 'number') {
    date = new Date(dateInput < 1e12 ? dateInput * 1000 : dateInput);
  } else {
    date = new Date(dateInput);
  }

  if (isNaN(date.getTime())) return '';

  return date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Возвращает относительное время (например: "2 часа назад")
 * @param {string|number} dateInput - ISO строка даты или Unix timestamp
 * @returns {string} Относительное время
 */
export function formatRelativeTime(dateInput) {
  if (!dateInput) return '';

  let date;

  if (typeof dateInput === 'number') {
    date = new Date(dateInput < 1e12 ? dateInput * 1000 : dateInput);
  } else {
    date = new Date(dateInput);
  }

  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffDay > 7) {
    return formatDate(dateInput);
  } else if (diffDay > 0) {
    return `${diffDay} ${pluralize(diffDay, 'день', 'дня', 'дней')} назад`;
  } else if (diffHour > 0) {
    return `${diffHour} ${pluralize(diffHour, 'час', 'часа', 'часов')} назад`;
  } else if (diffMin > 0) {
    return `${diffMin} ${pluralize(diffMin, 'минуту', 'минуты', 'минут')} назад`;
  } else {
    return 'Только что';
  }
}

/**
 * Форматирует сумму в тенге
 * @param {number} amount - сумма
 * @returns {string} Отформатированная сумма (например: "3 000 ₸")
 */
export function formatCurrency(amount) {
  const value = Number(amount) || 0;
  return `${value.toLocaleString('ru-RU')} ₸`;
}

/**
 * Склонение слов по числу
 */
function pluralize(n, one, few, many) {
  const mod10 = n % 10;
  const mod100 = n % 100;

  if (mod100 >= 11 && mod100 <= 19) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}
